import { createContext, useContext, useEffect, useRef, ReactNode, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import mixpanel from 'mixpanel-browser'
import { useAuth } from './AuthContext'

type TrackFn = (event: string, properties?: Record<string, unknown>) => void

interface AnalyticsContextValue {
  track: TrackFn
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null)

const MIXPANEL_TOKEN = '79ac105c927b6ec53b605a47f7614da0'

// Initialize Mixpanel eagerly so session replay starts capturing immediately
mixpanel.init(MIXPANEL_TOKEN, {
  track_pageview: false,
  persistence: 'localStorage',
  record_sessions_percent: 100,
  record_mask_all_text: false,
  record_mask_all_inputs: false,
  record_block_selector: '',
})

// Page name mapping from pathname
function getPageName(path: string): string {
  if (path === '/' || path === '/dashboard') return 'Dashboard'
  if (path.startsWith('/project/') && path.includes('/workspace')) return 'Workspace'
  if (path.startsWith('/project/')) return 'Project'
  if (path.startsWith('/showcase')) return 'Showcase'
  if (path.startsWith('/actors')) return 'Actors'
  if (path.startsWith('/settings')) return 'Settings'
  return 'Other'
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const location = useLocation()
  const prevUserIdRef = useRef<string | null>(null)

  // Identify user when they sign in, reset on sign out
  useEffect(() => {
    const currentUserId = user?.id ?? null

    if (user && currentUserId !== prevUserIdRef.current) {
      mixpanel.identify(user.email || user.id)
      mixpanel.people.set({
        $email: user.email,
        $name: user.user_metadata?.full_name || user.email,
        distinct_id: user.email || user.id,
        user_id: user.id,
        auth_provider: user.app_metadata?.provider || 'unknown',
        created_at: user.created_at,
      })
    } else if (!user && prevUserIdRef.current) {
      mixpanel.reset()
    }

    prevUserIdRef.current = currentUserId
  }, [user])

  // Track page views on route changes
  useEffect(() => {
    mixpanel.track('Page View', {
      path: location.pathname,
      page_name: getPageName(location.pathname),
    })
  }, [location.pathname])

  const track: TrackFn = useCallback((event, properties) => {
    mixpanel.track(event, properties)
  }, [])

  const value = useMemo(() => ({ track }), [track])

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export function useAnalytics(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}
